/**
 * Placeholder machinery for client-specific content that has not been supplied.
 *
 * Every unknown business fact is represented by a bracketed token such as
 * `[COACH NAME]`. Tokens are deliberately visible in the UI so that nobody
 * mistakes scaffolding for real content, and they are enumerable so that the
 * admin portal can report exactly what is still outstanding.
 *
 * Rules enforced by this module:
 *  - No business fact is ever invented. If it is unknown it is a token.
 *  - Tokens are overridable at runtime by admin-managed content.
 *  - `listUnresolvedPlaceholders` powers the admin content-readiness panel and
 *    is asserted against docs/CONTENT_REQUIRED.md by a unit test.
 */

/** Canonical list of every client-specific value the platform expects. */
export const PLACEHOLDER_KEYS = [
  'CLIENT BUSINESS NAME',
  'COACH NAME',
  'COACH MONOGRAM',
  'APP SLUG',
  'PUBLIC DOMAIN',
  'API DOMAIN',
  'BUSINESS EMAIL',
  'BUSINESS PHONE',
  'PRIMARY LOCATION',
  'SECONDARY LOCATION',
  'DEFAULT TIMEZONE',
  'DEFAULT CURRENCY',
  'COACH BIOGRAPHY',
  'COACH QUALIFICATIONS',
  'COACH EXPERIENCE',
  'COACH BOXING HISTORY',
  'COACH PERSONAL TRAINING HISTORY',
  'COACH NUTRITION SCOPE',
  'BUSINESS HOURS',
  'SESSION TYPES',
  'SESSION PRICES',
  'PACKAGE OPTIONS',
  'BOOKING POLICY',
  'CANCELLATION POLICY',
  'REFUND POLICY',
  'PRIVACY POLICY',
  'TERMS AND CONDITIONS',
  'TRAINING DISCLAIMER',
  'NUTRITION DISCLAIMER',
  'HEALTH SCREENING QUESTIONS',
  'PAR_Q_CONTENT',
  'LOGO PATH',
  'MONOGRAM PATH',
  'SOCIAL LINKS',
] as const;

export type PlaceholderKey = (typeof PLACEHOLDER_KEYS)[number];

/** A value that is knowingly not yet supplied by the client. */
export type Placeholder<K extends PlaceholderKey = PlaceholderKey> = `[${K}]`;

/** Builds a placeholder token. Prefer this over writing the literal by hand. */
export function placeholder<K extends PlaceholderKey>(key: K): Placeholder<K> {
  return `[${key}]`;
}

const PLACEHOLDER_PATTERN = /\[[A-Z0-9 _]+\]/;

/** True when `value` still contains an unresolved placeholder token. */
export function isPlaceholder(value: unknown): boolean {
  return typeof value === 'string' && PLACEHOLDER_PATTERN.test(value);
}

/**
 * Returns `fallback` only when `value` is absent, blank or itself a placeholder.
 * Used when merging admin-managed content over configuration defaults.
 *
 * Intentionally non-generic and widened to `string`: the fallbacks are string
 * *literal* types, and preserving those literals here would reject every real
 * CMS value at the call site.
 */
export function resolve(value: string | null | undefined, fallback: string): string {
  if (value === null || value === undefined) return fallback;
  const trimmed = value.trim();
  if (trimmed.length === 0 || isPlaceholder(trimmed)) return fallback;
  return trimmed;
}

export interface UnresolvedPlaceholder {
  /** Dotted path to the value, e.g. `coach.biography`. */
  path: string;
  /** The placeholder token found at that path. */
  token: string;
}

/**
 * Walks an arbitrary config tree and reports every unresolved placeholder with
 * its path. Arrays are indexed; cycles are guarded against.
 */
export function listUnresolvedPlaceholders(
  tree: unknown,
  basePath = '',
  seen: WeakSet<object> = new WeakSet(),
): UnresolvedPlaceholder[] {
  const found: UnresolvedPlaceholder[] = [];

  const walk = (node: unknown, path: string): void => {
    if (typeof node === 'string') {
      const match = PLACEHOLDER_PATTERN.exec(node);
      if (match) found.push({ path, token: match[0] });
      return;
    }
    if (node === null || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, `${path}[${index}]`));
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      walk(value, path ? `${path}.${key}` : key);
    }
  };

  walk(tree, basePath);
  return found;
}
